import { useState, useEffect, useCallback, useRef } from 'react';
import { Template, filterTemplates, ToolKey } from '@/data/templates-types';
import { supabase } from '@/integrations/supabase/client';

// Herramientas que el UI sabe pintar; lo demás se descarta en vez de romper iconos.
const VALID_TOOLS: ToolKey[] = [
  'n8n', 'openai', 'zapier', 'make', 'notion', 'sheets', 'gmail', 'slack',
  'discord', 'telegram', 'twilio', 'stripe', 'webhooks', 'github', 'whatsapp',
  'airtable', 'drive',
];

const youtubeId = (url: string | null): string => {
  if (!url) return '';
  const m = url.match(/(?:v=|youtu\.be\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : '';
};

// Filas añadidas desde /admin/workflows, convertidas a la forma que ya
// entiende todo el sitio. Ids desde 900000 para no caer en ningún rango
// de los filtros por id de CategoryPage.
const loadDbTemplates = async (): Promise<Template[]> => {
  const { data, error } = await supabase
    .from('templates')
    .select('name, title, description, creator, youtube_url, template_url, resource_url, slug, tags, tools, thumbnail')
    .order('created_at', { ascending: true });
  if (error || !data) return [];
  return data.map((r, i) => ({
    id: 900000 + i,
    slug: r.slug || r.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
    name: r.name,
    title: r.title,
    description: r.description || '',
    tags: r.tags || [],
    tools: (r.tools || []).filter((t): t is ToolKey => (VALID_TOOLS as string[]).includes(t)),
    youtubeUrl: r.youtube_url || '',
    youtubeId: youtubeId(r.youtube_url),
    thumbnail: r.thumbnail || '/images/templates/default.webp',
    downloadUrl: r.template_url || undefined,
    skoolUrl: r.resource_url || undefined,
    related: [],
  }));
};

// Lazy load static templates only when needed
let staticTemplatesCache: Template[] | null = null;
let loadingPromise: Promise<Template[]> | null = null;

const loadStaticTemplates = (): Promise<Template[]> => {
  if (staticTemplatesCache) return Promise.resolve(staticTemplatesCache);
  if (loadingPromise) return loadingPromise;
  
  loadingPromise = import('@/data/templates').then(module => {
    staticTemplatesCache = module.templates;
    return staticTemplatesCache;
  });
  
  return loadingPromise;
};

// Best Marketing Automations templates to include in search
const bestMarketingTemplates: Template[] = [
  {
    id: 1001,
    slug: "linkedin-brand-engine",
    name: "LinkedIn Brand Engine",
    title: "LinkedIn Brand Engine - Build Your Personal Brand Automatically",
    description: "Builds personal brand automatically with researched ideas, copy, and images",
    tags: ["LinkedIn", "Social Media", "Branding"],
    tools: ["sheets", "openai", "drive"],
    youtubeUrl: "https://www.youtube.com/watch?v=YpXzTSZ6csw",
    youtubeId: "YpXzTSZ6csw",
    thumbnail: "/images/templates/linkedin-brand.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=12dQ_BDHUge0-QYg0FjW4iT4Gbcc6YeTE&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=12dQ_BDHUge0-QYg0FjW4iT4Gbcc6YeTE&filename=flow.json",
    related: ["linkedin-dm-automation", "linkedin-performance-analyzer", "linkedin-cold-email-machine"]
  },
  {
    id: 1002,
    slug: "product-ad-generator",
    name: "Product Ad Generator",
    title: "Product Ad Generator - Studio-Quality Marketing Videos",
    description: "Turns product photos into professional ads, creates studio-quality marketing videos",
    tags: ["Ads", "Social Media", "Marketing"],
    tools: ["drive", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=qV7xOEKEBDA",
    youtubeId: "qV7xOEKEBDA",
    thumbnail: "/images/templates/product-ads.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1V8rmHA3VYreeMqQitqpUXSkHAMyc0dQN&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1V8rmHA3VYreeMqQitqpUXSkHAMyc0dQN&filename=flow.json",
    related: ["facebook-ads-generator", "facebook-ad-spy-system", "ai-marketing-team"]
  },
  {
    id: 1003,
    slug: "personalized-email-machine",
    name: "Personalized Email Machine",
    title: "Personalized Email Machine - Custom Cold Email Automation",
    description: "Generates custom cold emails for each lead, automates outreach completely",
    tags: ["Email Marketing", "Cold Outreach"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=oAWe5wFwHlo",
    youtubeId: "oAWe5wFwHlo",
    thumbnail: "/images/templates/email-automation.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1Oh0lUmNKmxPCXAcehIjRNxaeUnng_TFU&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1Oh0lUmNKmxPCXAcehIjRNxaeUnng_TFU&filename=flow.json",
    related: ["linkedin-cold-email-machine", "newsletter-automation-engine", "lead-generation-system"]
  },
  {
    id: 1004,
    slug: "facebook-ads-generator",
    name: "Facebook Ads Generator",
    title: "Facebook Ads Generator - VSL Scripts & Customer Feedback Analysis",
    description: "Analyzes ad performance, creates VSL scripts automatically, uses customer feedback",
    tags: ["Ads", "Facebook", "Marketing"],
    tools: ["airtable", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=Rm3ObJXsuOI",
    youtubeId: "Rm3ObJXsuOI",
    thumbnail: "/images/templates/facebook-ads.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1dbXhwS211LxAq9QcVs6kiHTpNHfje2NX&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1dbXhwS211LxAq9QcVs6kiHTpNHfje2NX&filename=flow.json",
    related: ["facebook-ad-spy-system", "product-ad-generator", "ai-marketing-team"]
  },
  {
    id: 1005,
    slug: "seo-blog-automation",
    name: "SEO Blog Automation System",
    title: "SEO Blog Automation - Cluster & Trend Posts Generator",
    description: "Generates cluster and trend posts automatically, saves SEO costs completely",
    tags: ["SEO", "Content", "Blogging"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=CP4VyckWlIg",
    youtubeId: "CP4VyckWlIg",
    thumbnail: "/images/templates/seo-automation.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=18j_SrKfiFeSu5gq-TFbi47JyAjbn-qup&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=18j_SrKfiFeSu5gq-TFbi47JyAjbn-qup&filename=flow.json",
    related: ["ai-seo-writer", "newsletter-automation-engine", "multi-platform-publisher"]
  },
  {
    id: 1006,
    slug: "veo3-ai-video-generator",
    name: "Veo3 AI Video Generator",
    title: "Veo3 AI Video Generator - Premium Automated Video Creation",
    description: "Generates premium videos with Veo3, automates best video creation available",
    tags: ["Social Media", "Video", "AI"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=4YuKnDum3eQ",
    youtubeId: "4YuKnDum3eQ",
    thumbnail: "/images/templates/veo3-video.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1XFmxJMbIz50y35YHs4fM6Ia5VyPr04ab&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1XFmxJMbIz50y35YHs4fM6Ia5VyPr04ab&filename=flow.json",
    related: ["4k-video-creator", "faceless-pov-video-creator", "youtube-long-form-generator"]
  },
  {
    id: 1007,
    slug: "multi-platform-publisher",
    name: "Multi-Platform Publisher",
    title: "Multi-Platform Publisher - 9 Social Platforms Automation",
    description: "Publishes your content across 9 social platforms automatically, saves hours daily",
    tags: ["Facebook", "Instagram", "LinkedIn", "Social Media", "TikTok"],
    tools: ["webhooks"],
    youtubeUrl: "https://www.youtube.com/watch?v=0qf0blCB4Mc",
    youtubeId: "0qf0blCB4Mc",
    thumbnail: "/images/templates/multi-platform.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1gH7Lik-0gXpRVPWKUscDDuS3vQANhHe2&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1gH7Lik-0gXpRVPWKUscDDuS3vQANhHe2&filename=flow.json",
    related: []
  },
  {
    id: 1008,
    slug: "auto-graphic-design-system",
    name: "Auto Graphic Design System",
    title: "Auto Graphic Design System - 24/7 Design Automation",
    description: "Makes logos, brand guides, backgrounds automatically, works 24/7 non-stop",
    tags: ["Design", "General", "Automation"],
    tools: ["drive", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=DrqR7a7lmBE",
    youtubeId: "DrqR7a7lmBE",
    thumbnail: "/images/templates/graphic-design.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1cIU63_yPJDM8xph4leB2UR9XxJwma-7C&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1cIU63_yPJDM8xph4leB2UR9XxJwma-7C&filename=flow.json",
    related: []
  },
  {
    id: 1009,
    slug: "newsletter-automation-engine",
    name: "Newsletter Automation Engine",
    title: "Newsletter Automation Engine - Weekly Content Generation",
    description: "Generates topics, writes quality content, sends newsletters automatically every week",
    tags: ["Email Marketing", "Content"],
    tools: ["gmail"],
    youtubeUrl: "https://www.youtube.com/watch?v=pxzo2lXhWJE",
    youtubeId: "pxzo2lXhWJE",
    thumbnail: "/images/templates/newsletter-automation.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1QFUXvG67dY68F40e6seZu6h4giUstyMh&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1QFUXvG67dY68F40e6seZu6h4giUstyMh&filename=flow.json",
    related: []
  },
  {
    id: 1010,
    slug: "ai-seo-writer",
    name: "AI SEO Writer",
    title: "AI SEO Writer - Optimized Blog Content Automation",
    description: "Writes optimized blog content automatically, publishes complete articles, dominates search",
    tags: ["SEO", "Content", "Blogging"],
    tools: ["openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=69qpNr2DX7Q",
    youtubeId: "69qpNr2DX7Q",
    thumbnail: "/images/templates/ai-seo-writer.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1VRfkmFo7XhAWe7r2s8aHcu-FPYUFL33y&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1VRfkmFo7XhAWe7r2s8aHcu-FPYUFL33y&filename=flow.json",
    related: []
  },
  {
    id: 1011,
    slug: "ai-clone-creator",
    name: "AI Clone Creator",
    title: "AI Clone Creator - Talking Avatar Content Generation",
    description: "Generates content ideas, produces your talking avatar, publishes everywhere",
    tags: ["Instagram", "Social Media", "TikTok"],
    tools: ["openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=m-pmZizBXzg",
    youtubeId: "m-pmZizBXzg",
    thumbnail: "/images/templates/ai-clone.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1Tvzgyc704fbx_KcbJkQ8j7oEgr3PSE8M&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1Tvzgyc704fbx_KcbJkQ8j7oEgr3PSE8M&filename=flow.json",
    related: []
  },
  {
    id: 1012,
    slug: "video-analyzer",
    name: "Video Analyzer",
    title: "Video Analyzer - Content Analysis & Marketing Trends",
    description: "Explains video content, answers specific questions, identifies marketing trends",
    tags: ["Social Media", "Analytics"],
    tools: ["openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=acB2o6nwAn8",
    youtubeId: "acB2o6nwAn8",
    thumbnail: "/images/templates/video-analyzer.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=12Qd7XwOcka_DUcD8jiFkmFoN3Be1Gkc4&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=12Qd7XwOcka_DUcD8jiFkmFoN3Be1Gkc4&filename=flow.json",
    related: []
  },
  {
    id: 1013,
    slug: "linkedin-dm-automation",
    name: "LinkedIn DM Automation System",
    title: "LinkedIn DM Automation - AI-Powered Lead Messaging",
    description: "Scrapes leads, personalizes LinkedIn messages with AI, sends DMs automatically",
    tags: ["Cold Outreach", "LinkedIn"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=gsPGdq2C97c",
    youtubeId: "gsPGdq2C97c",
    thumbnail: "/images/templates/linkedin-dm.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1LlwSvGXRz1S1TLsq105iT0_WxDEHigPh&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1LlwSvGXRz1S1TLsq105iT0_WxDEHigPh&filename=flow.json",
    related: []
  },
  {
    id: 1014,
    slug: "instagram-viral-searcher",
    name: "Instagram Viral Searcher",
    title: "Instagram Viral Searcher - Viral Pattern Analysis",
    description: "Analyzing what makes niche creators go viral, organizing patterns into your database",
    tags: ["Instagram", "Social Media", "Analytics"],
    tools: ["airtable"],
    youtubeUrl: "https://www.youtube.com/watch?v=zEzcwjZf7c8",
    youtubeId: "zEzcwjZf7c8",
    thumbnail: "/images/templates/instagram-viral.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1CeMYnGoLFLlsMoFWRZ8FsWLlaKxIXgEo&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1CeMYnGoLFLlsMoFWRZ8FsWLlaKxIXgEo&filename=flow.json",
    related: []
  },
  {
    id: 1015,
    slug: "ai-marketing-team",
    name: "AI Marketing Team",
    title: "AI Marketing Team - Complete Marketing Automation",
    description: "Replaces marketing team, creates images and content, generates videos automatically",
    tags: ["General", "Social Media", "Marketing"],
    tools: ["drive", "sheets", "openai", "telegram"],
    youtubeUrl: "https://www.youtube.com/watch?v=ldETapkr8Hg",
    youtubeId: "ldETapkr8Hg",
    thumbnail: "/images/templates/ai-marketing-team.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=10tMD9TO0Ah34Mj80jmflLBHV-0MFB-MT&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=10tMD9TO0Ah34Mj80jmflLBHV-0MFB-MT&filename=flow.json",
    related: []
  },
  {
    id: 1016,
    slug: "facebook-ad-spy-system",
    name: "Facebook Ad Spy System",
    title: "Facebook Ad Spy System - Winning Formula Analysis",
    description: "Scrapes top ads, explains winning formulas, rewrites for high-paying clients",
    tags: ["Ads", "Facebook"],
    tools: ["sheets", "openai", "drive"],
    youtubeUrl: "https://www.youtube.com/watch?v=0PJNiDuihBg",
    youtubeId: "0PJNiDuihBg",
    thumbnail: "/images/templates/facebook-ad-spy.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1pRVQ8UFDSzUYljLi_XmsnPLbVx_UVoes&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1pRVQ8UFDSzUYljLi_XmsnPLbVx_UVoes&filename=flow.json",
    related: []
  },
  {
    id: 1017,
    slug: "4k-video-creator",
    name: "4K Video Creator",
    title: "4K Video Creator - Premium Quality Enhancement",
    description: "Transforms videos to 4K 60fps, enhances quality automatically, delivers premium results",
    tags: ["Social Media", "YouTube", "Video"],
    tools: ["sheets"],
    youtubeUrl: "https://www.youtube.com/watch?v=mFmX5LJ0rdI",
    youtubeId: "mFmX5LJ0rdI",
    thumbnail: "/images/templates/4k-video-creator.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=14UlGUC2BqvzS92C8ZPJrMHVE66dazIPS&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=14UlGUC2BqvzS92C8ZPJrMHVE66dazIPS&filename=flow.json",
    related: []
  },
  {
    id: 1018,
    slug: "tiktok-viral-searcher",
    name: "TikTok Viral Searcher",
    title: "TikTok Viral Searcher - Viral Pattern Analysis",
    description: "Finding top outlier videos in your niche and organizing viral concepts in a database",
    tags: ["Social Media", "TikTok", "Analytics"],
    tools: ["airtable"],
    youtubeUrl: "https://www.youtube.com/watch?v=OSpw9AY1fJI",
    youtubeId: "OSpw9AY1fJI",
    thumbnail: "/images/templates/tiktok-viral-searcher.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=14E6Y-RBeEL0S_juvVFg11bDGxKjPAmEt&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=14E6Y-RBeEL0S_juvVFg11bDGxKjPAmEt&filename=flow.json",
    related: []
  },
  {
    id: 1019,
    slug: "youtube-long-form-generator",
    name: "YouTube Long Form Generator",
    title: "YouTube Long Form Generator - Viral Content Creation",
    description: "Creates viral city ranking videos, generates slideshow content, drives massive traffic",
    tags: ["Social Media", "YouTube", "Content"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=lF2bvXoV-Zg",
    youtubeId: "lF2bvXoV-Zg",
    thumbnail: "/images/templates/youtube-long-form.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1KCUSiQRjDDB-iktHTmzY4Knl4SvsZq8O&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1KCUSiQRjDDB-iktHTmzY4Knl4SvsZq8O&filename=flow.json",
    related: []
  },
  {
    id: 1020,
    slug: "lead-generation-system",
    name: "Lead Generation System",
    title: "Lead Generation System - Smart Lead Scraping",
    description: "Scrapes any lead through chat, creates detailed prospect reports, cheaper than Apollo",
    tags: ["Cold Outreach", "Lead Generation"],
    tools: ["sheets", "openai", "telegram"],
    youtubeUrl: "https://www.youtube.com/watch?v=P9TfIvtYExQ",
    youtubeId: "P9TfIvtYExQ",
    thumbnail: "/images/templates/lead-generation-system.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1zhzQRwSftheqMQYF5QuoW0kEmKVUME_g&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1zhzQRwSftheqMQYF5QuoW0kEmKVUME_g&filename=flow.json",
    related: []
  },
  {
    id: 1021,
    slug: "instagram-idea-scraper",
    name: "Instagram Idea Scraper",
    title: "Instagram Idea Scraper - Content Strategy Automation",
    description: "Scrapes successful content, creates better scripts, grows followers from zero",
    tags: ["Instagram", "Social Media", "Content"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=9zBtU1mwOR4",
    youtubeId: "9zBtU1mwOR4",
    thumbnail: "/images/templates/instagram-idea-scraper.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1E326sTsdMbUeyw_f43XOUP9WYXFZN3Fu&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1E326sTsdMbUeyw_f43XOUP9WYXFZN3Fu&filename=flow.json",
    related: []
  },
  {
    id: 1022,
    slug: "linkedin-cold-email-machine",
    name: "LinkedIn Cold Email Machine",
    title: "LinkedIn Cold Email Machine - Simplified Lead Outreach",
    description: "Scrapes LinkedIn data, launches email campaigns, much simpler than before",
    tags: ["Cold Outreach", "LinkedIn", "Email Marketing"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=bFxWRkWAFzs",
    youtubeId: "bFxWRkWAFzs",
    thumbnail: "/images/templates/linkedin-cold-email.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1H1kZaYe_NOOj2KHlHWSzy-TvDvO6X_VO&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1H1kZaYe_NOOj2KHlHWSzy-TvDvO6X_VO&filename=flow.json",
    related: []
  },
  {
    id: 1023,
    slug: "linkedin-performance-analyzer",
    name: "LinkedIn Performance Analyzer",
    title: "LinkedIn Performance Analyzer - Competitive Intelligence",
    description: "Tracks your and competitors performance, finds winning patterns, suggests ideas",
    tags: ["LinkedIn", "Social Media", "Analytics"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=GmlQZ8MG7HY",
    youtubeId: "GmlQZ8MG7HY",
    thumbnail: "/images/templates/linkedin-performance.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1EeacVDcHu2KwOXAb-9CcQBkqsOuJr-VG&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1EeacVDcHu2KwOXAb-9CcQBkqsOuJr-VG&filename=flow.json",
    related: []
  },
  {
    id: 1024,
    slug: "faceless-pov-video-creator",
    name: "Faceless POV Video Creator",
    title: "Faceless POV Video Creator - Viral Video Automation",
    description: "Creates trending POV videos automatically, posts on autopilot, generates viral content",
    tags: ["Instagram", "Social Media", "YouTube", "Video"],
    tools: ["sheets"],
    youtubeUrl: "https://www.youtube.com/watch?v=OTKtKkFax30",
    youtubeId: "OTKtKkFax30",
    thumbnail: "/images/templates/faceless-pov-video.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=18qeFFHosBaEaO5E5DrKA5x1jQwtnZ6tK&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=18qeFFHosBaEaO5E5DrKA5x1jQwtnZ6tK&filename=flow.json",
    related: []
  },
  {
    id: 1025,
    slug: "free-apollo-lead-scraper",
    name: "Free Apollo Lead Scraper",
    title: "Free Apollo Lead Scraper - No Subscription Required",
    description: "Generates leads without Apollo subscription, finds emails automatically, saves to sheets",
    tags: ["Cold Outreach", "Lead Generation"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=GNw0fWkekEI",
    youtubeId: "GNw0fWkekEI",
    thumbnail: "/images/templates/apollo-lead-scraper.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=17iIu7-FO5RK2BVdnKITem_YQYe8WfaaM&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=17iIu7-FO5RK2BVdnKITem_YQYe8WfaaM&filename=flow.json",
    related: []
  },
  {
    id: 1026,
    slug: "youtube-content-strategist",
    name: "YouTube Content Strategist",
    title: "YouTube Content Strategist - Market Analysis & Ideas",
    description: "Studies market trends, reviews your analytics, creates personalized video concepts",
    tags: ["Social Media", "YouTube", "Content", "Analytics"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=Ch-AWxvX2Jc",
    youtubeId: "Ch-AWxvX2Jc",
    thumbnail: "/images/templates/youtube-strategist.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1T0mF19gth9XsdraRRbiKhgcpAf-7Sz0_&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1T0mF19gth9XsdraRRbiKhgcpAf-7Sz0_&filename=flow.json",
    related: []
  },
  {
    id: 1027,
    slug: "veo3-video-automation-system",
    name: "Veo3 Video Automation System",
    title: "Veo3 Video Automation System - Cost-Effective Creation",
    description: "Creates videos cheapest way possible, adapts to any niche, makes any duration",
    tags: ["Social Media", "Video", "AI"],
    tools: ["sheets"],
    youtubeUrl: "https://www.youtube.com/watch?v=6aU-hz5BxPQ",
    youtubeId: "6aU-hz5BxPQ",
    thumbnail: "/images/templates/veo3-automation.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1c5_mopaR4zvOQDYh_t10Le0W0pP39xFL&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1c5_mopaR4zvOQDYh_t10Le0W0pP39xFL&filename=flow.json",
    related: []
  },
  {
    id: 1028,
    slug: "ai-ad-content-creator",
    name: "AI Ad Content Creator",
    title: "AI Ad Content Creator - Bulk Ad Generation",
    description: "Makes 100 marketing ads from single image, multiplies content automatically",
    tags: ["Ads", "Marketing", "Content"],
    tools: ["airtable"],
    youtubeUrl: "https://www.youtube.com/watch?v=gXEYKIwk7j8",
    youtubeId: "gXEYKIwk7j8",
    thumbnail: "/images/templates/ai-ad-content.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1YtnlWP8bqkCPPD8PfIWEvE8zK0nR78Vn&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1YtnlWP8bqkCPPD8PfIWEvE8zK0nR78Vn&filename=flow.json",
    related: []
  },
  {
    id: 1029,
    slug: "ai-copywriting-team",
    name: "AI Copywriting Team",
    title: "AI Copywriting Team - Specialized Marketing Copy",
    description: "Specialized AI writers, generates marketing copy for all use cases",
    tags: ["General", "Marketing", "Content"],
    tools: ["openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=GoDmjHs7o-M",
    youtubeId: "GoDmjHs7o-M",
    thumbnail: "/images/templates/ai-copywriting-team.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1TZ8fWmLPVBzbiNtIvpLaopwgXDn7f91X&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1TZ8fWmLPVBzbiNtIvpLaopwgXDn7f91X&filename=flow.json",
    related: []
  },
  {
    id: 1030,
    slug: "animal-battle-shorts-creator",
    name: "Animal Battle Shorts Creator",
    title: "Animal Battle Shorts Creator - Viral Entertainment",
    description: "Creates viral animal fight videos, generates engaging battle content automatically",
    tags: ["Social Media", "TikTok", "YouTube", "Entertainment"],
    tools: ["webhooks"],
    youtubeUrl: "https://www.youtube.com/watch?v=jkEEVYFzT1U",
    youtubeId: "jkEEVYFzT1U",
    thumbnail: "/images/templates/animal-battle-shorts.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1-jNBRZzU94liM7wURzD5C4k8uYqQWHNL&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1-jNBRZzU94liM7wURzD5C4k8uYqQWHNL&filename=flow.json",
    related: []
  },
  {
    id: 1031,
    slug: "twitter-thread-automation-system",
    name: "Twitter Thread Automation System",
    title: "Twitter Thread Automation - Viral Thread Generator",
    description: "Generates trending threads, curates news content, creates viral Twitter posts",
    tags: ["Social Media", "Twitter", "Content"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=X1CVUuPog4A",
    youtubeId: "X1CVUuPog4A",
    thumbnail: "/images/templates/twitter-thread-automation.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1JXwpXNW2-FMjI1VQmDxEoxOcuvrII8iX&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1JXwpXNW2-FMjI1VQmDxEoxOcuvrII8iX&filename=flow.json",
    related: []
  },
  {
    id: 1032,
    slug: "viral-shorts-generator",
    name: "Viral Shorts Generator",
    title: "Viral Shorts Generator - Trending Content Creation",
    description: "Makes trending president videos, creates YouTube shorts content, posts automatically",
    tags: ["Social Media", "YouTube", "Viral"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=oMcLgseGER0",
    youtubeId: "oMcLgseGER0",
    thumbnail: "/images/templates/viral-shorts-generator.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1ugMXqTCBcON9IlL2jXtnmpT1X5MR3avq&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1ugMXqTCBcON9IlL2jXtnmpT1X5MR3avq&filename=flow.json",
    related: []
  },
  {
    id: 1033,
    slug: "seedance-ai-video-generator",
    name: "Seedance AI Video Generator",
    title: "Seedance AI Video Generator - Cost-Effective Quality",
    description: "Creates top-quality videos 25x cheaper than Veo3, uses newest AI model",
    tags: ["Social Media", "YouTube", "Video", "AI"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=hbHNScyJnbo",
    youtubeId: "hbHNScyJnbo",
    thumbnail: "/images/templates/seedance-ai-video.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1TeoipYvKh3rEmx9dA6wSvkhhuulI9sKf&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1TeoipYvKh3rEmx9dA6wSvkhhuulI9sKf&filename=flow.json",
    related: []
  },
  {
    id: 1034,
    slug: "cold-email-bulk-personalizer",
    name: "Cold Email Bulk Personalizer",
    title: "Cold Email Bulk Personalizer - Scale Email Outreach",
    description: "Sends personalized emails at scale, eliminates expensive email software costs",
    tags: ["Cold Outreach", "Email Marketing"],
    tools: ["gmail", "sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=BMA2F9Gc2Cc",
    youtubeId: "BMA2F9Gc2Cc",
    thumbnail: "/images/templates/cold-email-bulk.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1NjNjOorqHzw8RryQItsbGrBedojwPRPY&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1NjNjOorqHzw8RryQItsbGrBedojwPRPY&filename=flow.json",
    related: []
  },
  {
    id: 1035,
    slug: "carousel-auto-poster",
    name: "Carousel Auto-Poster",
    title: "Carousel Auto-Poster - Visual Content Automation",
    description: "Creates carousels, posts to TikTok and Instagram, eliminates manual design",
    tags: ["Instagram", "Social Media", "TikTok"],
    tools: ["openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=mzxdtDCZbNY",
    youtubeId: "mzxdtDCZbNY",
    thumbnail: "/images/templates/carousel-auto-poster.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1AmWW4ElVUtJOIPsF0mLL9-c9RV28zsdy&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1AmWW4ElVUtJOIPsF0mLL9-c9RV28zsdy&filename=flow.json",
    related: []
  },
  {
    id: 1036,
    slug: "asmr-shorts-generator",
    name: "ASMR Shorts Generator",
    title: "ASMR Shorts Generator - Satisfying Content Creation",
    description: "Creates viral knife cutting videos, generates satisfying ASMR content automatically",
    tags: ["Social Media", "ASMR", "Viral"],
    tools: ["sheets", "openai"],
    youtubeUrl: "https://www.youtube.com/watch?v=RvuPRX-b7Lc",
    youtubeId: "RvuPRX-b7Lc",
    thumbnail: "/images/templates/asmr-shorts-generator.webp",
    downloadUrl: "https://drive.google.com/uc?export=download&id=1YrT0ixQR1YImtcoUtx0rCLsRdgrct6tq&filename=flow.json",
    skoolUrl: "https://drive.google.com/uc?export=download&id=1YrT0ixQR1YImtcoUtx0rCLsRdgrct6tq&filename=flow.json",
    related: []
  }
];

export function useTemplates() {
  // Start with marketing templates for instant display
  const [templates, setTemplates] = useState<Template[]>(bestMarketingTemplates);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const fetchTemplates = useCallback(async () => {
    if (hasLoadedRef.current) return;
    
    try {
      setError(null);
      
      // Lazy load static templates
      const staticTemplates = await loadStaticTemplates();

      // Los añadidos desde el admin (Supabase). Si la red falla, sigue sin ellos.
      const dbTemplates = await loadDbTemplates().catch(() => [] as Template[]);

      // Combine static templates with best marketing automations + admin rows.
      // Dedupe por slug: si una fila del admin repite un slug estático, gana la del admin.
      const base = [...staticTemplates, ...bestMarketingTemplates];
      const dbSlugs = new Set(dbTemplates.map(t => t.slug));
      const allTemplates = [...base.filter(t => !dbSlugs.has(t.slug)), ...dbTemplates];
      setTemplates(allTemplates);
      hasLoadedRef.current = true;

    } catch (err) {
      console.error('Unexpected error fetching templates:', err);
      setError('An unexpected error occurred');
      // Keep marketing templates as fallback
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Use requestIdleCallback for non-blocking load
    if ('requestIdleCallback' in window) {
      const idleId = requestIdleCallback(() => fetchTemplates(), { timeout: 2000 });
      return () => cancelIdleCallback(idleId);
    } else {
      // Fallback with setTimeout
      const timeoutId = setTimeout(fetchTemplates, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [fetchTemplates]);

  const searchTemplates = useCallback((searchQuery: string) => {
    return filterTemplates(templates, searchQuery);
  }, [templates]);

  return {
    templates,
    loading,
    error,
    searchTemplates,
    refetch: fetchTemplates
  };
}